import React from 'react'
import DocumentDialog, { DocumentSection } from '../DocumentDialog'

interface PackingListDialogNewProps {
  packingListData: any
  schema: any
  onSave?: (updatedData: any, changes?: any[]) => void
  shipmentId: string
  documentId: string
}

const PackingListDialog: React.FC<PackingListDialogNewProps> = ({
  packingListData,
  schema,
  onSave,
  shipmentId,
  documentId,
}) => {
  // Define sections for Packing List
  const sections: DocumentSection[] = [
    {
      id: 'shipmentInfo',
      title: 'Shipment Information',
      layout: 'double',
      fields: [
        { type: 'field', label: 'ID', path: 'shipmentDetails.shipmentId' },
        { type: 'field', label: 'Date', path: 'shipmentDetails.shipmentDate' },
        { type: 'field', label: 'Method', path: 'shipmentDetails.shipmentMethod' },
        { type: 'field', label: 'Carrier', path: 'shipmentDetails.carrierName' },
        { type: 'field', label: 'Tracking', path: 'shipmentDetails.trackingNumber' },
        { type: 'field', label: 'Expected Delivery', path: 'shipmentDetails.expectedDeliveryDate' },
        { type: 'field', label: 'Incoterms', path: 'shipmentDetails.incoterms' },
      ],
    },
    {
      id: 'referenceNumbers',
      title: 'Reference Numbers',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Purchase Order', path: 'referenceNumbers.purchaseOrderNumber' },
        { type: 'field', label: 'Sales Order', path: 'referenceNumbers.salesOrderNumber' },
        { type: 'field', label: 'Invoice', path: 'referenceNumbers.invoiceNumber' },
        { type: 'field', label: 'Bill of Lading', path: 'referenceNumbers.billOfLadingNumber' },
        { type: 'field', label: 'Customer Ref', path: 'referenceNumbers.customerReferenceNumber' },
      ],
    },
    {
      id: 'parties',
      title: 'Parties',
      layout: 'grid',
      fields: [
        {
          type: 'object',
          label: 'Shipper',
          path: 'shipper',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'City', path: 'city' },
            { label: 'State', path: 'state' },
            { label: 'ZIP Code', path: 'zipCode' },
            { label: 'Country', path: 'country' },
            { label: 'Phone', path: 'phone' },
            { label: 'Email', path: 'email' },
            { label: 'Tax ID', path: 'taxId' },
          ],
        },
        {
          type: 'object',
          label: 'Consignee',
          path: 'consignee',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'City', path: 'city' },
            { label: 'State', path: 'state' },
            { label: 'ZIP Code', path: 'zipCode' },
            { label: 'Country', path: 'country' },
            { label: 'Phone', path: 'phone' },
            { label: 'Email', path: 'email' },
            { label: 'Tax ID', path: 'taxId' },
          ],
        },
      ],
    },
    {
      id: 'shipmentDetails',
      title: 'Shipment Details',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Total Weight', path: 'shipmentDetails.totalWeight', complexType: 'weight' },
        { type: 'field', label: 'Total Volume', path: 'shipmentDetails.totalVolume', complexType: 'volume' },
        { type: 'field', label: 'Number of Packages', path: 'shipmentDetails.numberOfPackages' },
        { type: 'field', label: 'Special Instructions', path: 'shipmentDetails.specialInstructions', fullWidth: true },
      ],
    },
    {
      id: 'items',
      title: 'Items',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Items',
          path: 'items',
          columns: [
            { label: 'Line Number', path: 'lineNumber' },
            { label: 'Description', path: 'description' },
            { label: 'Quantity', path: 'quantity' },
            { label: 'Unit of Measure', path: 'unitOfMeasure' },
            {
              label: 'Unit Price',
              path: 'unitPrice',
              complexType: 'currency',
            },
            {
              label: 'Total Price',
              path: 'totalPrice',
              complexType: 'currency',
            },
            { label: 'SKU', path: 'sku' },
            { label: 'Package Number', path: 'packageNumber' },
            { label: 'Country of Origin', path: 'countryOfOrigin' },
            { label: 'HS Code', path: 'hsCode' },
            {
              label: 'Weight',
              path: 'weight',
              complexType: 'weight',
            },
            {
              label: 'Volume',
              path: 'volume',
              complexType: 'volume',
            },
            {
              label: 'Dimensions',
              path: 'dimensions',
              complexType: 'dimensions',
            },
          ],
        },
      ],
    },
    {
      id: 'customsInfo',
      title: 'Customs Information',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Customs Information',
          path: 'customsInfo',
          fields: [
            { label: 'Export License Number', path: 'exportLicenseNumber' },
            { label: 'Declaration Statement', path: 'declarationStatement' },
            { label: 'Total Customs Value', path: 'totalCustomsValue' },
            { label: 'Harmonized Tariff Schedule', path: 'harmonizedTariffSchedule' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'signatures',
      title: 'Signatures & Approvals',
      layout: 'double',
      fields: [
        {
          type: 'object',
          label: 'Signatures',
          path: 'signatures',
          fields: [
            { label: 'Prepared By', path: 'preparedBy' },
            { label: 'Approved By', path: 'approvedBy' },
            { label: 'Date Approved', path: 'dateApproved' },
            { label: 'Shipper Signature', path: 'shipperSignature' },
            { label: 'Receiver Signature', path: 'receiverSignature' },
            { label: 'Date Received', path: 'dateReceived' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'additionalInfo',
      title: 'Additional Information',
      layout: 'single',
      fields: [
        { type: 'field', label: 'Notes', path: 'notes', fullWidth: true },
        { type: 'field', label: 'Terms', path: 'terms', fullWidth: true },
        { type: 'field', label: 'Is Hazardous Material', path: 'isHazardousMaterial' },
        { type: 'field', label: 'Hazardous Material Info', path: 'hazardousMaterialInfo', fullWidth: true },
      ],
    },
  ]

  return (
    <DocumentDialog
      documentData={packingListData}
      schema={schema}
      sections={sections}
      documentType="Packing List"
      onSave={onSave}
      shipmentId={shipmentId}
      documentId={documentId}
    />
  )
}

export default PackingListDialog
